import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MarketTrendPoint } from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { formatUsd } from '../../lib/format';

/**
 * SIL-304 (W4, financials-hifi-flow-spec.md slice 6) — 12-18mo market-price
 * trend line for one SKU. `medianPriceUsd` is null for any month whose comp
 * count fell below the k-anonymity floor (or had no comps at all) — spec
 * §Market-comp disclosure policy forbids disclosing which, so both render
 * identically: a gap in the line (`connectNulls={false}`), never an
 * interpolated/fabricated value bridging the gap.
 */
export function MarketTrendLineChart({
  trend,
  height = 180,
}: {
  trend: MarketTrendPoint[];
  height?: number;
}) {
  const hasAnyPoint = trend.some((p) => p.medianPriceUsd !== null);

  if (!hasAnyPoint) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={trend} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={false} />
        <YAxis
          stroke="#94a3b8"
          fontSize={12}
          tickLine={false}
          width={52}
          tickFormatter={(v) => formatUsd(v as number, { compact: true })}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: '#e4e4e7' }}
          formatter={(v) =>
            v === null || v === undefined
              ? (['Insufficient depth', 'Median'] as [string, string])
              : ([formatUsd(v as number), 'Median'] as [string, string])
          }
        />
        <Line
          dataKey="medianPriceUsd"
          name="Median"
          stroke={SX_PALETTE[0]}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
